// ChangePassword — self-service password change, force-shown on first login when
// the backend sets must_change_password=true on the user record.
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2 } from 'lucide-react';
import { API } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const forced = new URLSearchParams(location.search).get('forced') === '1';
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error('New password must be at least 8 characters');
    if (next !== confirm) return toast.error('Passwords do not match');
    if (next === current) return toast.error('New password must differ from current');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next });
      toast.success('Password changed successfully.');
      if (refresh) await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="animate-fadeIn" data-testid="change-password-page" style={{ maxWidth: 480, margin: '32px auto' }}>
      <div className="form-card">
        <div style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Lock className="w-5 h-5" style={{ color: 'var(--heading)' }} />
            <div className="page-title" style={{ margin: 0, fontSize: 20 }}>Change Password</div>
          </div>
          {forced && (
            <div style={{ padding: 12, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#78350f', marginBottom: 16 }}>
              For security, you must change your temporary password before continuing.
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Current password</label>
              <input type="password" value={current} onChange={e => setCurrent(e.target.value)} required
                     data-testid="cp-current"
                     style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>New password <span style={{ color: '#888' }}>(8+ chars)</span></label>
              <input type="password" value={next} onChange={e => setNext(e.target.value)} required minLength={8}
                     data-testid="cp-new"
                     style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Confirm new password</label>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8}
                     data-testid="cp-confirm"
                     style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            </div>
            <button type="submit" disabled={submitting} data-testid="cp-submit"
                    style={{ height: 40, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer', marginTop: 6 }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" style={{ display: 'inline' }} /> : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
